<template>
  <div
    class="rg-last-object-tracking-container flex flex-col gap-2"
    :style="styleObject"
    ref="rgLastObjectTrackingContainer"
  >
    <div
      class="tracking-body cursor-move flex flex-row justify-start items-center gap-2 p-2 aisea-text-14-400-white"
      @mousedown.prevent.stop="mouseDownHandler"
      @touchstart.prevent.stop="mouseDownHandler"
    >
      <div class="obj-ic flex items-center">
        <img src="@/assets/images/radarGrid/track_vessel.svg" />
      </div>
      <div class="obj-info flex gap-3">
        <div class="obj-name aisea-text-14-400-white flex items-center">
          {{ trackNo }}
        </div>
        <div class="obj-time aisea-text-14-600-white">+{{ elapsedTime }}</div>
      </div>
      <div class="tracking-body-btn-wrapper ml-auto">
        <vx-tooltip
          text="ターゲット一覧"
          position="right"
          :color="AISEA_COLOR.GARY_BLACK"
          class="aisea-text-12-400-white"
        >
          <button
            class="tracking-body-btn"
            @click.prevent.stop="handleClickTrackingBodyBtn"
            @touchstart.prevent.stop="handleClickTrackingBodyBtn"
          ></button>
        </vx-tooltip>
      </div>
    </div>
    <component :is="scrollbarTag" class="flex-grow-0" :key="$vs.rtl">
      <div class="current-danger-objects flex flex-col gap-2">
        <rg-object-list-item
          v-for="{ id, n, k, r, p, rgRiskLevelManager } in dangerObjects"
          :key="id"
          :trackNumber="n"
          :kind="k"
          :riskLevel="r"
          :rgRiskLevelManager="rgRiskLevelManager"
          :pictures="p"
          :handleClick="() => handleClickListItem(id)"
          :timeCount="timeCount"
        ></rg-object-list-item>
      </div>
    </component>
  </div>
</template>

<script>
import orderBy from "lodash/orderBy";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { RadarGridMixin, DeviceInfoMixin, ColorMixin } from "@/mixins";
import RgObjectListItem from "./rg-object-list-container/RgObjectListItem.vue";
import isNumber from "lodash/isNumber";
import isDate from "lodash/isDate";
const RG_LAST_OBJECT_TRACKING_CONTAINER_MIN_LEFT = 0;
const RG_LAST_OBJECT_TRACKING_CONTAINER_MIN_TOP = 0;
const INITIAL_ELAPSED_TIME = "00:00:00";

export default {
  name: "RgLastObjectTracking",
  mixins: [RadarGridMixin, DeviceInfoMixin, ColorMixin],
  components: { RgObjectListItem, VuePerfectScrollbar },
  props: {
    trackNo: String,
    startTime: String,
    radarGridObjectFirestoreData: {
      type: Object, // Object<RadarGridObject.id, RadarGridObject>
      required: true,
    },
    rgRiskLevelManagerMap: {
      type: Object, // Object<RadarGridObject.id, RgRiskLevelManager>
      required: true,
    },
    handleClickTrackingBodyBtn: {
      type: Function,
      required: true,
    },
    handleClickListItem: {
      type: Function,
      required: true,
    },
    timeCount: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      styleObject: {
        top: "144px",
        left: "20px",
        position: "absolute",
      },
      interval: null,
      x: null,
      y: null,
    };
  },

  watch: {
    onChangeWindowSize() {
      this.$nextTick(async () => {
        this.adjustRgLastObjectTrackingContainerPosition();
      });
    },
    timeCount() {
      this.interval = this.getInterval();
    },
  },

  computed: {
    scrollbarTag() {
      return this.$store.state.is_touch_device ? "div" : "VuePerfectScrollbar";
    },
    dangerObjects() {
      if (!this.radarGridObjectFirestoreData) return [];
      return orderBy(
        Object.values(this.radarGridObjectFirestoreData)
          .filter((radarObject) => radarObject)
          .filter(({ id }) => this.rgRiskLevelManagerMap[id])
          .map((radarObject) => ({
            ...radarObject,
            rgRiskLevelManager: this.rgRiskLevelManagerMap[radarObject.id],
          }))
          .filter(({ rgRiskLevelManager }) =>
            this.isDanger(rgRiskLevelManager.riskLevel),
          ),
        [
          (radarObject) => {
            return (
              Array.isArray(radarObject.p) &&
              radarObject.p[0] &&
              radarObject.p[0].t &&
              radarObject.p[0].t.toMillis()
            );
          },
        ],
        ["desc"],
      );
    },
    start() {
      if (!this.startTime) return null;
      return new Date(this.startTime);
    },
    elapsedTime() {
      return this.getTimeStr();
    },
  },

  methods: {
    getInterval() {
      if (!isDate(this.start)) return null;
      return new Date() - this.start;
    },

    getTimeStr() {
      if (!isNumber(this.interval)) return INITIAL_ELAPSED_TIME;
      let seconds = Math.floor((this.interval / 1000) % 60);
      let minutes = Math.floor((this.interval / (60 * 1000)) % 60);
      let hours = Math.floor(this.interval / (60 * 60 * 1000));
      seconds = ("0" + seconds).slice(-2);
      minutes = ("0" + minutes).slice(-2);
      hours = hours < 100 ? ("0" + hours).slice(-2) : hours;

      return `${hours}:${minutes}:${seconds}`;
    },

    mouseDownHandler(e) {
      e.preventDefault();
      e.stopPropagation();

      const event = this.getEventFromMouseOrTouch(e);
      this.x = event.clientX;
      this.y = event.clientY;
      document.addEventListener("mousemove", this.mouseMoveHandler);
      document.addEventListener("mouseup", this.mouseUpHandler);

      document.addEventListener("touchmove", this.mouseMoveHandler, {
        passive: false,
      });
      document.addEventListener("touchend", this.mouseUpHandler, {
        passive: false,
      });
    },

    mouseMoveHandler(e) {
      e.preventDefault();
      e.stopPropagation();

      const el = this.rgLastObjectTrackingContainer();
      if (el) {
        const event = this.getEventFromMouseOrTouch(e);

        if (this.styleObject.position === "absolute") {
          this.styleObject.position = "fixed";
          this.styleObject.left = `${el.getBoundingClientRect().x}px`;
        } else {
          const dx = event.clientX - this.x;
          this.styleObject.left = `${Math.max(
            Math.min(el.offsetLeft + dx, this.maxX()),
            RG_LAST_OBJECT_TRACKING_CONTAINER_MIN_TOP,
          )}px`;
        }
        this.x = event.clientX;

        const dy = event.clientY - this.y;
        this.styleObject.top = `${Math.max(
          Math.min(el.offsetTop + dy, this.maxY()),
          RG_LAST_OBJECT_TRACKING_CONTAINER_MIN_LEFT,
        )}px`;
        this.y = event.clientY;
      }
    },

    mouseUpHandler(e) {
      e.preventDefault();
      e.stopPropagation();

      this.removeEventListeners();
    },

    rgLastObjectTrackingContainer() {
      if (!this.$refs) return undefined;
      return this.$refs.rgLastObjectTrackingContainer;
    },

    setClientLeftTop() {
      const rgLastObjectTrackingContainer =
        this.rgLastObjectTrackingContainer();
      if (rgLastObjectTrackingContainer) {
        this.x = this.rgLastObjectTrackingContainer().clientLeft;
        this.y = this.rgLastObjectTrackingContainer().clientTop;
      }
    },

    getEventFromMouseOrTouch(e) {
      return this.isTouchEvent(e) ? e.changedTouches[0] : e;
    },

    isTouchEvent(e) {
      return e && e.constructor && e.constructor.name === "TouchEvent";
    },

    maxX() {
      if (!this.rgLastObjectTrackingContainer()) {
        return document.body.clientWidth;
      }
      return (
        document.body.clientWidth -
        this.rgLastObjectTrackingContainer().clientWidth
      );
    },

    maxY() {
      if (!this.rgLastObjectTrackingContainer()) {
        return document.body.clientHeight;
      }
      return (
        document.body.clientHeight -
        this.rgLastObjectTrackingContainer().clientHeight
      );
    },

    removeEventListeners() {
      document.removeEventListener("mousemove", this.mouseMoveHandler);
      document.removeEventListener("mouseup", this.mouseUpHandler);

      document.removeEventListener("touchmove", this.mouseMoveHandler, {
        passive: false,
      });
      document.removeEventListener("touchend", this.mouseUpHandler, {
        passive: false,
      });
    },

    adjustRgLastObjectTrackingContainerPosition() {
      const el = this.rgLastObjectTrackingContainer();
      if (el) {
        this.styleObject.left = `${Math.max(
          Math.min(el.offsetLeft, this.maxX()),
          RG_LAST_OBJECT_TRACKING_CONTAINER_MIN_TOP,
        )}px`;

        this.styleObject.top = `${Math.max(
          Math.min(el.offsetTop, this.maxY()),
          RG_LAST_OBJECT_TRACKING_CONTAINER_MIN_LEFT,
        )}px`;
      }
    },
  },

  mounted() {
    this.setClientLeftTop();
  },

  beforeDestroy() {
    this.removeEventListeners();
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/aisea/_variables.scss";

.rg-last-object-tracking-container {
  z-index: 3;
  right: 20px;
  width: 256px !important;
  max-height: 595px;
  overflow-y: hidden;

  .tracking-body {
    width: 256px !important;
    height: 44px !important;
    background-color: $aisea-black;
    border-radius: 5px;

    .obj-ic {
      grid-area: obj-ic;
      align-self: center;
      justify-self: center;

      img {
        display: block;
        margin: auto;
      }
    }

    .tracking-body-btn {
      // reset button style
      all: unset;
      outline: revert;
      display: block;

      height: 28px;
      width: 28px;
      background-color: $aisea-gray;
      border-radius: 17.7px;
      border: none;
      outline: none;
      background-image: url("~@/assets/images/radarGrid/last_tracking_object_button.svg");
      background-repeat: no-repeat;
      background-position: center;

      &:hover {
        cursor: pointer;
        background-color: $aisea-gray-black;
      }

      &:active {
        background-color: $aisea-gray-black;
      }
    }
  }
}
</style>
